import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Film, Mail, Lock, User, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import usePageMeta from '@/hooks/usePageMeta';
import { AuthPlanTiers } from '@/components/auth/AuthPlanTiers';
import { AuthIntroCopy } from '@/components/auth/AuthIntroCopy';

// Determine initial mode from either pathname (/signup, /login) or ?mode= query.
function resolveInitialAuthMode(pathname, searchParams) {
  if (pathname === '/signup') return 'signup';
  if (pathname === '/login') return 'login';
  const queryMode = searchParams.get('mode');
  return queryMode === 'signup' ? 'signup' : 'login';
}

function getSubmitLabel(loading, mode) {
  if (loading) return 'Please wait...';
  return mode === 'login' ? 'Sign In' : 'Create Account';
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const initialMode = resolveInitialAuthMode(location.pathname, searchParams);
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  // Canonical path — use the clean URL when available so search engines
  // consolidate `/auth?mode=signup` and `/signup` into a single URL.
  const canonicalPath = mode === 'signup' ? '/signup' : '/login';

  usePageMeta(
    mode === 'signup'
      ? {
          title: 'Sign Up for Free — Final Frame | Indie Film Production Management',
          description: 'Create a free Final Frame account and start planning your next indie film. Upload scripts, break down scenes, plan shoot days, and send call sheets — no credit card required.',
          path: canonicalPath,
        }
      : {
          title: 'Sign In to Your Final Frame Account',
          description: 'Sign in to Final Frame to access your indie film projects, script breakdowns, shooting schedules, call sheets, and production documents.',
          path: canonicalPath,
        }
  );

  // Optional redirect back after successful auth (used by /founding CTA)
  const redirectTo = (() => {
    const r = searchParams.get('redirect') || '';
    // Same-origin path only
    return r.startsWith('/') && !r.startsWith('//') ? r : '';
  })();

  useEffect(() => {
    if (user) navigate(redirectTo || '/dashboard', { replace: true });
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'signup' && !name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'signup') {
        const payload = { email, password, name };
        if (phone.trim() && smsConsent) {
          payload.phone = phone.trim();
          payload.sms_consent = true;
        }
        const res = await api.post('/auth/signup', payload);
        setUser(res.data.user);
        toast.success('Welcome aboard.');
      } else {
        const res = await api.post('/auth/login', { email, password });
        setUser(res.data.user);
        toast.success('Welcome back.');
      }
      navigate(redirectTo || '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong';
      if (detail.includes('already registered')) {
        setError('This email is already registered. Try signing in instead.');
      } else if (detail.includes('Invalid credentials')) {
        setError('Wrong email or password. Check your details and try again.');
      } else if (detail.includes('Google')) {
        setError('This account uses Google sign-in. Click "Continue with Google" below.');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + (redirectTo || '/dashboard');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] px-4 py-10" data-testid="auth-page">
      <div className={`mx-auto animate-fade-in ${mode === 'signup' ? 'max-w-6xl' : 'max-w-2xl'}`}>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#A0A0A0] hover:text-[#F2F2F2] mb-8 transition-colors font-body text-sm"
          data-testid="auth-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Film className="w-6 h-6 text-[#D6B36A]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-[#F2F2F2]">Final Frame</span>
        </div>

        <div className={`${mode === 'signup' ? 'grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 mt-6' : ''}`}>
          {/* Left column — signup / login form */}
          <div>
            <h1 className="font-heading text-3xl text-[#F2F2F2] mb-2 mt-2">
              {mode === 'login' ? 'Welcome back to Final Frame' : 'Sign up for free and start your production'}
            </h1>
            <p className="text-[#A0A0A0] text-sm mb-6 font-body">
              {mode === 'login' ? 'Pick up where you left off.' : 'Start planning your next film.'}
            </p>

        {/* Tab Toggle */}
        <div className="flex bg-[#151515] rounded-lg p-1 mb-6 max-w-sm" data-testid="auth-mode-toggle">
          <button
            onClick={() => { setMode('signup'); setError(''); }}
            className={`flex-1 py-2.5 text-sm font-body font-medium rounded-md transition-all ${mode === 'signup' ? 'bg-[#D6B36A] text-[#0D0D0D]' : 'text-[#A0A0A0] hover:text-[#F2F2F2]'}`}
            data-testid="auth-tab-signup"
          >
            Create Account
          </button>
          <button
            onClick={() => { setMode('login'); setError(''); }}
            className={`flex-1 py-2.5 text-sm font-body font-medium rounded-md transition-all ${mode === 'login' ? 'bg-[#D6B36A] text-[#0D0D0D]' : 'text-[#A0A0A0] hover:text-[#F2F2F2]'}`}
            data-testid="auth-tab-login"
          >
            Sign In
          </button>
        </div>

        {/* Inline Error */}
        {error && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 max-w-sm" data-testid="auth-error">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-red-400 font-body text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 max-w-sm">
          {mode === 'signup' && (
            <div>
              <Label className="text-[#A0A0A0] text-xs uppercase tracking-wider font-body">Name</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555555]" />
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="pl-10 bg-[#151515] border-white/10 text-[#F2F2F2] placeholder:text-[#555555] focus:border-[#D6B36A] focus-visible:ring-0 focus-visible:ring-offset-0" required data-testid="auth-name-input" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-[#A0A0A0] text-xs uppercase tracking-wider font-body">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555555]" />
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 bg-[#151515] border-white/10 text-[#F2F2F2] placeholder:text-[#555555] focus:border-[#D6B36A] focus-visible:ring-0 focus-visible:ring-offset-0" required data-testid="auth-email-input" />
            </div>
          </div>
          <div>
            <Label className="text-[#A0A0A0] text-xs uppercase tracking-wider font-body">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555555]" />
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" className="pl-10 bg-[#151515] border-white/10 text-[#F2F2F2] placeholder:text-[#555555] focus:border-[#D6B36A] focus-visible:ring-0 focus-visible:ring-offset-0" required minLength={6} data-testid="auth-password-input" />
            </div>
          </div>
          {mode === 'signup' && (
            <div className="space-y-3 pt-1">
              <div>
                <Label className="text-[#A0A0A0] text-xs uppercase tracking-wider font-body">Phone <span className="text-[#555555] normal-case">(optional)</span></Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" inputMode="tel" className="mt-1.5 bg-[#151515] border-white/10 text-[#F2F2F2] placeholder:text-[#555555] focus:border-[#D6B36A] focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="auth-phone-input" />
              </div>
              {phone.trim() && (
                <div className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={e => setSmsConsent(e.target.checked)}
                      className="mt-1 accent-[#D6B36A] shrink-0"
                      data-testid="auth-sms-consent"
                    />
                    <p className="text-[#A0A0A0] font-body text-[11px] leading-relaxed">
                      I agree to receive SMS messages from Final Frame regarding my production projects, including call sheets, production updates, and other account-related notifications. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out and HELP for help. Consent is not a condition of purchase.
                    </p>
                  </div>
                  <p className="text-[#555555] font-body text-[10px] leading-relaxed pl-6">
                    Optional. Not required to create an account or use Final Frame. View our{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D6B36A] hover:underline" data-testid="auth-sms-privacy-link">Privacy Policy</a>
                    {' '}and{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D6B36A] hover:underline" data-testid="auth-sms-terms-link">Terms &amp; Conditions</a>.
                  </p>
                </div>
              )}
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-[#D6B36A] text-[#0D0D0D] hover:bg-[#E8C881] font-body font-medium transition-all duration-200 hover:scale-[1.01] mt-2" data-testid="auth-submit-btn">
            {getSubmitLabel(loading, mode)}
          </Button>

          {mode === 'login' && (
            <div className="text-center pt-1">
              <a
                href="/forgot-password"
                onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}
                className="text-[#A0A0A0] hover:text-[#D6B36A] font-body text-xs transition-colors"
                data-testid="auth-forgot-password-link"
              >
                Forgot your password?
              </a>
            </div>
          )}
        </form>

        <div className="relative my-5 max-w-sm">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
          <div className="relative flex justify-center"><span className="px-4 bg-[#0D0D0D] text-[#555555] text-xs font-body">or</span></div>
        </div>

        <Button variant="outline" onClick={handleGoogleAuth} className="w-full max-w-sm border-white/10 text-[#F2F2F2] hover:bg-white/5 font-body" data-testid="auth-google-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        <p className="text-[#555555] text-xs mt-4 font-body max-w-sm">
          Free to get started. No credit card required.
        </p>

        {mode === 'signup' && <AuthIntroCopy mode="signup" />}

        {mode === 'login' && <AuthIntroCopy mode="login" />}
          </div>

          {/* Right column — plan tiers (signup mode only) */}
          {mode === 'signup' && <AuthPlanTiers />}
        </div>
      </div>
    </div>
  );
}
